import { useCallback, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router';

import { SearchParamKeys } from '~/constants/url';
import { useCraftLocalStorage } from '~/hooks/useCraftLocalStorage';

/**
 * Captures the LeadJourney click id from the URL and persists it, so a completed registration can be
 * attributed back to the campaign that brought the freelancer here.
 *
 * LeadJourney appends `lj_click_id` when linking from the website to the platform. Because registration
 * spans multiple steps (and freelancers may abandon it and return later), the click id is kept in
 * local storage rather than read from the URL at submit time. The most recent click wins, and the stored
 * value is cleared once a registration completes so it can never be reused for a later, unrelated one.
 */
export const useLeadJourneyClickId = () => {
  const [searchParams] = useSearchParams();
  const searchParamsClickId = searchParams.get(SearchParamKeys.LEAD_JOURNEY_CLICK_ID);

  const [clickId, setClickId] = useCraftLocalStorage('leadJourneyClickId');

  useEffect(() => {
    if (searchParamsClickId) setClickId(searchParamsClickId);
  }, [searchParamsClickId, setClickId]);

  const clearClickId = useCallback(() => {
    setClickId(null);
  }, [setClickId]);

  return useMemo(() => ({ clickId, clearClickId }), [clearClickId, clickId]);
};
